/**
 * WebTracingManager - Singleton that manages web log providers
 *
 * Holds per-provider log level settings and routes log entries
 * to enabled providers based on their configured level.
 */

import {
  LogLevel,
  WebComponentCode,
  type WebTracingBackend,
  type WebTracingConfig,
  type WebTracingProviderConfig,
  type WebLogProvider,
  type WebLogEntry,
  type WebAppSource,
} from './types';

const DEFAULT_CONFIG: WebTracingConfig = {
  providers: {
    'web-console': { enabled: true, logLevel: LogLevel.DEBUG },
    'web-server': { enabled: true, logLevel: LogLevel.INFO1 },
    'web-popup': { enabled: true, logLevel: LogLevel.WARN },
  },
  componentLogLevels: {},
};

class WebTracingManagerImpl {
  private providers = new Map<WebTracingBackend, WebLogProvider>();
  private providerConfigs = new Map<WebTracingBackend, WebTracingProviderConfig>();
  private componentLogLevels = new Map<WebComponentCode, LogLevel>();
  /**
   * Verbosity floors set by the client itself rather than by remote config —
   * e.g. a feature gate turning one user's canvas tracing up.
   *
   * Kept separate because remote config arrives asynchronously and replaces
   * `componentLogLevels` wholesale; a floor written before it lands would
   * otherwise be silently erased a moment later.
   */
  private localComponentFloors = new Map<WebComponentCode, LogLevel>();
  private _sessionId: string;
  private _source: WebAppSource = 'web-student';

  constructor() {
    this._sessionId = this.generateSessionId();
    this.applyConfig(DEFAULT_CONFIG);
  }

  get sessionId(): string {
    return this._sessionId;
  }

  get source(): WebAppSource {
    return this._source;
  }

  setSource(source: WebAppSource): void {
    this._source = source;
  }

  /** Register a provider implementation */
  registerProvider(provider: WebLogProvider): void {
    this.providers.set(provider.name, provider);
  }

  /** Apply config from backend or defaults */
  updateConfig(config: WebTracingConfig): void {
    this.applyConfig(config);
  }

  /** Check if a log at the given level + component would be emitted to any provider */
  isLevelEnabled(level: LogLevel, component: WebComponentCode): boolean {
    for (const [backend, config] of this.providerConfigs) {
      if (!config.enabled) continue;
      const effectiveLevel = this.getEffectiveLevel(backend, component);
      if (level <= effectiveLevel) return true;
    }
    return false;
  }

  /** Route a log entry to all enabled providers that accept its level */
  log(entry: WebLogEntry): void {
    for (const [backend, provider] of this.providers) {
      const config = this.providerConfigs.get(backend);
      if (!config?.enabled) continue;
      const effectiveLevel = this.getEffectiveLevel(backend, entry.component);
      if (entry.level <= effectiveLevel) {
        try {
          provider.log(entry);
        } catch {
          // Never let a provider crash break the app
        }
      }
    }
  }

  /** Flush all providers (e.g., on app unmount) */
  async flush(): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const provider of this.providers.values()) {
      if (provider.flush) {
        promises.push(provider.flush());
      }
    }
    await Promise.allSettled(promises);
  }

  /** Cleanup all providers */
  destroy(): void {
    for (const provider of this.providers.values()) {
      if (provider.destroy) {
        provider.destroy();
      }
    }
    this.providers.clear();
  }

  private applyConfig(config: WebTracingConfig): void {
    // Apply provider configs
    for (const [backend, providerConfig] of Object.entries(config.providers)) {
      if (providerConfig) {
        this.providerConfigs.set(backend as WebTracingBackend, providerConfig);
      }
    }

    // Apply component log levels
    this.componentLogLevels.clear();
    for (const [codeStr, level] of Object.entries(config.componentLogLevels)) {
      if (level !== undefined) {
        this.componentLogLevels.set(Number(codeStr) as WebComponentCode, level);
      }
    }
  }

  /**
   * Raise a component's verbosity locally, on top of whatever remote config
   * says. Never lowers it — a floor can only add detail, so this cannot be used
   * to silence a component that the server wants logged.
   */
  setLocalComponentFloor(component: WebComponentCode, level: LogLevel): void {
    const current = this.localComponentFloors.get(component);
    if (current === undefined || level > current) {
      this.localComponentFloors.set(component, level);
    }
  }

  /** Get effective log level for a provider+component pair */
  private getEffectiveLevel(backend: WebTracingBackend, component: WebComponentCode): LogLevel {
    // Component override takes priority
    const componentLevel = this.componentLogLevels.get(component);
    const configured = componentLevel !== undefined
      ? componentLevel
      : this.providerConfigs.get(backend)?.logLevel ?? LogLevel.INFO1;
    // A local floor only ever adds verbosity — higher numbers are more verbose.
    const floor = this.localComponentFloors.get(component);
    return floor !== undefined && floor > configured ? floor : configured;
  }

  private generateSessionId(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const segments = [8, 4, 4];
    return segments
      .map((len) => Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join(''))
      .join('-');
  }
}

/** Singleton instance */
let instance: WebTracingManagerImpl | null = null;

export function getWebTracingManager(): WebTracingManagerImpl {
  if (!instance) {
    instance = new WebTracingManagerImpl();
  }
  return instance;
}
